import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { Bar, Line } from "react-chartjs-2";
import { Legend, Tooltip } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
} from "chart.js";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  annotationPlugin,
);

const token = localStorage.getItem("token");

const PARAMETERS = {
  rain: "Rainfall (in)",
  t2m: "Air Temperature (°F)",
  rh: "Humidity (%)",
  ws: "Wind Speed (mph)",
  et: "Evapotranspiration (in)",
  rfd: "Radiation Flux Density (W/m²)",
  gdd: "Growing Degree Days (°F)",
};

const forecastKeyMap = {
  t2m: "avgtemp_f",
  rain: "totalprecip_in",
  rh: "avghumidity",
  ws: "maxwind_mph",
};

const COLORS = {
  rain: "#99ccff",
  rfd: "#FF8C00",
  rh: "#FF1493",
  ws: "#FF4500",
  et: "#32CD32",
  t2m: "#1E90FF",
  gdd: "#228B22",
  cumulativeGdd: "#800080",
};

const WeatherGraph = () => {
  const [selectedParam, setSelectedParam] = useState("rain");
  const [dbChartData, setDbChartData] = useState(null);
  const [dbTableData, setDbTableData] = useState([]);
  const [forecastStartLabel, setForecastStartLabel] = useState(null);
  const [allChartLabels, setAllChartLabels] = useState([]);

  const formatDateMMDD = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString !== "string") {
      const parsed = new Date(dateString);
      return Number.isNaN(parsed.getTime())
        ? ""
        : parsed.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
          });
    }

    const raw = dateString.split("T")[0];

    // Already MM/DD/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return raw;
    }

    // ISO YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${month}/${day}/${year}`;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime())
      ? raw
      : parsed.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
        });
  };

  const chartScrollRef = useRef(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await axios.get(
          `/api/weatherchartdata?parameter=${selectedParam}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const { chartData, tableData } = response.data;

        setAllChartLabels(chartData.labels);
        setDbTableData(tableData);

        // Set forecast start label if forecast exists
        if (chartData.forecastStartIndex >= 0) {
          setForecastStartLabel(chartData.labels[chartData.forecastStartIndex]);
        } else {
          setForecastStartLabel(null);
        }

        // Build Chart.js datasets
        if (selectedParam === "gdd") {
          setDbChartData({
            labels: chartData.labels,
            datasets: [
              {
                label: "GDD (Daily)",
                data: chartData.datasets[0].data,
                borderColor: COLORS["t2m"],
                backgroundColor: "transparent",
                borderWidth: 2,
                fill: false,
                tension: 0,
                yAxisID: "y",
              },
              {
                label: "Cumulative GDD",
                data: chartData.datasets[1].data,
                borderColor: COLORS["cumulativeGdd"],
                backgroundColor: "transparent",
                borderWidth: 2,
                fill: false,
                tension: 0.3,
                yAxisID: "y1",
              },
            ],
          });
        } else {
          // Single combined dataset for all parameters (including rain)
          setDbChartData({
            labels: chartData.labels,
            datasets: [
              {
                label: PARAMETERS[selectedParam],
                data: chartData.datasets[0].data,
                borderColor: COLORS[selectedParam],
                backgroundColor:
                  selectedParam === "rain" ? COLORS.rain : "transparent",
                borderWidth: 2,
                fill: selectedParam !== "rain",
                tension: 0,
                maxBarThickness: selectedParam === "rain" ? 40 : undefined,
              },
            ],
          });
        }
      } catch (error) {
        console.error("Error fetching chart data", error);
      }
    };

    fetchChartData();
  }, [selectedParam]);

  useEffect(() => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
    }
  }, [dbChartData]);

  const chartOptions = useMemo(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      responsive: true,
      layout: { padding: { right: 20, top: 30 } },
      plugins: {
        legend: {
          display: selectedParam === "gdd",
          position: "right",
          labels: {
            font: { size: 16 },
            color: "#333",
            boxWidth: 20,
          },
        },
        ...(forecastStartLabel &&
          allChartLabels.length > 0 && {
            annotation: {
              annotations: {
                forecastDivider: {
                  type: "line",
                  scaleID: "x",
                  value: forecastStartLabel,
                  borderColor: "gray",
                  borderWidth: 2,
                  borderDash: [6, 6],
                  label: {
                    content: "Forecast Start",
                    enabled: true,
                    position: "start",
                    font: {
                      style: "italic",
                      size: 14,
                    },
                    color: "#555",
                    yAdjust: -10,
                  },
                },
                forecastRegion: {
                  type: "box",
                  xMin: forecastStartLabel,
                  xMax: allChartLabels[allChartLabels.length - 1],
                  backgroundColor: "rgba(173, 216, 230, 0.15)",
                  borderWidth: 0,
                },
              },
            },
          }),
      },
      scales: {
        x: {
          type: "category",
          reverse: false,
          offset: true,
          grid: {
            offset: true,
          },
          ticks: {
            font: { size: 20 },
            autoSkip: false,
            maxRotation: 0,
            padding: 10,
            align: "center",
          },
          title: { display: true, text: "Date", font: { size: 30 } },
        },
        y: {
          position: "left",
          display: false,
          title: {
            display: false,
            text:
              selectedParam === "gdd"
                ? "GDD (°F)"
                : PARAMETERS[selectedParam] || "Value",
            font: { size: 30 },
          },
          ticks: { font: { size: 20 } },
          ...(selectedParam === "gdd" && {
            min: 0,
            suggestedMax:
              dbChartData?.datasets[1]?.data
                ?.filter((v) => v !== null)
                .slice(-1)[0] || 100,
          }),
        },
        y1: {
          position: "right",
          display: selectedParam === "gdd",
          grid: { drawOnChartArea: false },
          title: {
            display: selectedParam === "gdd",
            text: "Cumulative GDD (°F)",
            font: { size: 30 },
          },
          ticks: { font: { size: 20 } },
          ...(selectedParam === "gdd" && {
            min: 0,
            suggestedMax:
              dbChartData?.datasets[1]?.data
                ?.filter((v) => v !== null)
                .slice(-1)[0] || 100,
          }),
        },
      },
    }),
    [selectedParam, forecastStartLabel, allChartLabels],
  );

  const yAxisChartData = useMemo(
    () => ({
      labels: dbChartData?.labels || [],
      datasets: [
        {
          label: "Placeholder",
          data: dbChartData?.datasets[0]?.data || [],
          borderColor: "transparent",
          backgroundColor: "transparent",
        },
      ],
    }),
    [dbChartData],
  );

  const yAxisChartOptions = useMemo(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      layout: {
        padding: { top: 30 }, // match padding of main chart
      },
      scales: {
        x: {
          display: false,
          offset: true,
          grid: {
            offset: true,
          },
          ticks: {
            align: "center",
          },
        },
        y: {
          position: "left",
          display: true,
          title: {
            display: true,
            text:
              selectedParam === "gdd"
                ? "GDD (°F)"
                : PARAMETERS[selectedParam] || "Value",
            font: { size: 30 },
          },
          ticks: { font: { size: 20 } },
          padding: { right: 70 },
          grid: {
            display: false,
            drawTicks: true,
            drawBorder: true,
          },
        },
      },
    }),
    [selectedParam],
  );

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        {Object.keys(PARAMETERS).map((param) => (
          <div
            key={param}
            onClick={() => setSelectedParam(param)}
            style={{
              cursor: "pointer",
              padding: "10px 16px",
              backgroundColor: selectedParam === param ? "#fa4616" : "#F5F5F5",
              border:
                selectedParam === param
                  ? "2px solid rgb(255, 255, 255)"
                  : "2px solid rgb(37, 106, 185)",
              borderRadius: "12px",
              color: selectedParam === param ? "white" : "#333",
              boxShadow:
                selectedParam === param
                  ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                  : "none",
              transition: "all 0.3s ease-in-out",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              height: "50px",
            }}
          >
            <span>{PARAMETERS[param]}</span>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: "0px",
          width: "100%",
          maxHeight: "500px",
          flexDirection: window.innerWidth < 768 ? "column" : "row",
        }}
      >
        <div
          style={{
            width: window.innerWidth < 768 ? "100%" : "170px",
            display: window.innerWidth < 768 ? "none" : "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            maxHeight: "400px",
            paddingTop: "3px",
          }}
        >
          {dbChartData && (
            <div style={{ width: "100%", height: "100%" }}>
              {selectedParam === "rain" ? (
                <Bar data={yAxisChartData} options={yAxisChartOptions} />
              ) : (
                <Line data={yAxisChartData} options={yAxisChartOptions} />
              )}
            </div>
          )}
        </div>

        {/* Main Chart */}
        {dbChartData && (
          <div
            ref={chartScrollRef}
            style={{
              flexGrow: 1,
              overflowX: "auto",
              paddingTop: window.innerWidth < 768 ? "10px" : "25px",
              width: window.innerWidth < 768 ? "100%" : "auto",
            }}
          >
            <div
              style={{
                width:
                  window.innerWidth < 768
                    ? `${dbChartData.labels.length * 100}px`
                    : `${dbChartData.labels.length * 100}px`,
                height: window.innerWidth < 768 ? "300px" : "470px",
                minWidth:
                  window.innerWidth < 768
                    ? `${dbChartData.labels.length * 100}px`
                    : "auto",
              }}
            >
              {selectedParam === "rain" ? (
                <Bar data={dbChartData} options={chartOptions} />
              ) : (
                <Line data={dbChartData} options={chartOptions} />
              )}
            </div>
          </div>
        )}

        {/* DB Data Table Container */}
        <div
          style={{
            minWidth: window.innerWidth < 768 ? "100%" : "300px",
            maxWidth: window.innerWidth < 768 ? "100%" : "300px",
            flexShrink: 0,
            height: window.innerWidth < 768 ? "auto" : "500px",
            maxHeight: window.innerWidth < 768 ? "400px" : "500px",
            overflowY: "auto",
            backgroundColor: "#f4f4f4",
            borderRadius: "10px",
            paddingLeft: window.innerWidth < 768 ? "15px" : "30px",
            paddingRight: window.innerWidth < 768 ? "15px" : "30px",
            boxSizing: "border-box",
            marginTop: window.innerWidth < 768 ? "20px" : "0",
          }}
        >
          <h3
            style={{ fontSize: "24px", fontWeight: "bold", color: "#002657" }}
          >
            {PARAMETERS[selectedParam]}
          </h3>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              borderRadius: "10px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#002657", color: "white" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                  }}
                >
                  Date
                </th>
                {selectedParam === "gdd" ? (
                  <>
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      GDD (°F)
                    </th>
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      Cumulative GDD (°F)
                    </th>
                  </>
                ) : (
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    {PARAMETERS[selectedParam]}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {dbTableData.map((entry, index) => (
                <tr key={index}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    {formatDateMMDD(entry.date)}
                  </td>
                  {selectedParam === "gdd" ? (
                    <>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "10px",
                          textAlign: "center",
                        }}
                      >
                        {typeof entry.gdd === "number"
                          ? entry.gdd.toFixed(2)
                          : entry.gdd}
                      </td>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "10px",
                          textAlign: "center",
                        }}
                      >
                        {typeof entry.cumulativeGdd === "number"
                          ? entry.cumulativeGdd.toFixed(2)
                          : entry.cumulativeGdd}
                      </td>
                    </>
                  ) : (
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      {typeof entry.value === "number"
                        ? entry.value.toFixed(2)
                        : entry.value}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default React.memo(WeatherGraph);
